import React, { Ref, useEffect, useId } from "react";
import InputMask from '@mona-health/react-input-mask';
import CurrencyInput from 'react-currency-input-field';
import { MaskedInput } from "react-text-mask-modern"
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import { MaskTypes } from "../utils/models";
import "./styles.scss";
import TextInput, { TextInputProps } from "./TextInput";
import { ValidatorForm } from "react-material-ui-form-validator";
import { extractSSN } from "../utils/dataConverter";
import { InputBaseComponentProps } from "@mui/material";

interface TextMaskCustomProps {
  name: string;
  value: string;
  onChange: (e: any) => void;
}

const phoneNoMask = '(999) 999-9999';
const ssnMask = '999-99-9999';
const creditCardNumberMask = '9999 9999 9999 9999';
const creditCardExpiryMask = '99/99';
const creditCardCVCMask = '9999';
const employerTaxIdMask = '99-9999999';

const TextMaskCustom = ({
  name,
  value,
  onChange,
  ref,
  ...other
}: InputBaseComponentProps & {ref: Ref<InputBaseComponentProps>}) => {

  const onFocusReset = () => {
    if (Number(value) === 0) {
      !!onChange && (onChange as any)({ target: { value: "" } });
    }
  };

  if (name == MaskTypes.Dollar) {
    
    return <CurrencyInput
      {...other}
      ref={ref as any}
      defaultValue={other.defaultValue as string | number | undefined}
      disableAbbreviations={true}
      decimalsLimit={2}
      onFocus={onFocusReset}
      value={value}
      onChange={(evt) => onChange?.call(null, evt)}
      maxLength={9}
      prefix="$"
    />
  }

  if (name == MaskTypes.Refund) {
    const defaultMaskOptions = {
      prefix: "$",
      suffix: "",
      includeThousandsSeparator: true,
      thousandsSeparatorSymbol: ",",
      allowDecimal: false,
      integerLimit: 7, // limit length of integer numbers
      allowNegative: false,
      allowLeadingZeroes: false,
    };

    return <MaskedInput
      {...other}
      ref={ref as any}
      mask={createNumberMask(defaultMaskOptions)}
      placeholderChar={"x"}
      showMask={true}
      keepCharPositions={true}
      guide={false}
      onFocus={onFocusReset}
      value={value}
      onChange={onChange}
    />
  }

  const setMask = (type: string) => {
    switch (type) {
      case MaskTypes.PhoneNo:
        return phoneNoMask;
      case MaskTypes.SSN:
        return ssnMask;
      case MaskTypes.CreditCardNumber:
        return creditCardNumberMask;
      case MaskTypes.CreditCardExpiry:
        return creditCardExpiryMask;
      case MaskTypes.CreditCardCVC:
        return creditCardCVCMask;
      case MaskTypes.EmployerTaxId:
        return employerTaxIdMask;
      default:
        return phoneNoMask;
    }
  };

  return (
    <InputMask
      {...other}
      mask={setMask(name)}
      maskPlaceholder=" "
      value={value}
      onChange={onChange}
    />
  );
};

const ForwardedTextMaskCustom = React.forwardRef<any, InputBaseComponentProps>((props, ref) => {
  return TextMaskCustom({ ...props, ref });
})

type MaskTextInputProps = TextInputProps & {
  maskType: MaskTypes;
  ariaRequired?: boolean;
};

const MaskTextInput = ({
  maskType,
  className,
  ariaRequired,
  ...props
}: MaskTextInputProps) => {

  useEffect(() => {
    ValidatorForm.addValidationRule('isSSN', (value: any) => {
      const val = extractSSN(value);
      return !!val && val.length === 9 && !isNaN(Number(val));
    });
  }, []);

  const getPlaceholder = (type: string) => {
    switch (type) {
      case MaskTypes.PhoneNo:
        return "(xxx)-xxx-xxxx";
      case MaskTypes.SSN:
        return "xxx-xx-xxxx";
      case MaskTypes.Refund:
      case MaskTypes.Dollar:
        return "$0.00";
      case MaskTypes.CreditCardNumber:
        return "xxxx xxxx xxxx xxxx";
      case MaskTypes.CreditCardExpiry:
        return "00/00";
      case MaskTypes.CreditCardCVC:
        return "CVC code";
      case MaskTypes.EmployerTaxId:
        return "xx-xxxxxxx";
      default:
        return "+xx (xxx) xxx - xxxx";
    }
  };


  const inputMode = (type: string) => {
    switch (type) {
      case MaskTypes.PhoneNo:
      case MaskTypes.SSN:
      case MaskTypes.CreditCardNumber:
      case MaskTypes.CreditCardExpiry:
      case MaskTypes.CreditCardCVC:
      case MaskTypes.EmployerTaxId:
        return "numeric";
      case MaskTypes.Refund:
      case MaskTypes.Dollar:
        return "decimal";
      default:
        return "text";
    }
  }

  const id = 'input' + useId();

  return (
    <TextInput
      id={id}
      className={`masked-input ${className}`}
      fullWidth={true}
      placeholder={getPlaceholder(maskType)}
      slotProps={{
        input: { inputComponent: ForwardedTextMaskCustom },
        htmlInput: {
          inputMode: inputMode(maskType),
          "aria-required": ariaRequired,
        }
      }}
      {...props}
      name={maskType}
    />
  );
};

export default MaskTextInput;
